import type { SkillId } from './heroes.ts';
import { SKILLS } from './heroes.ts';
import { SKILL_CASTS, type FieldPatch } from './skill-casts.ts';
import type { SkillField } from './skill-cast.ts';
import type { DamageType } from './damage.ts';

/**
 * Glyphs (docs/GLIFOS.md): a mark carved on a skill button that changes the form or the damage type of whatever skill
 * of any class it fits. One slot per button, opened with the hero's level; each glyph once per hero; free to change
 * in a preparation; three fragments complete one. Stage 1: the glyphs that only change numbers; stage 2: the ones that
 * turn what a skill deals into another damage type; stage 3: the ones that change how it reaches (docs/GLIFOS.md §1, Forma).
 */
export type GlyphId = 'prolong' | 'celerity' | 'amplitude' | 'focus' | 'ember' | 'rime' | 'storm' | 'venom' | 'echo' | 'fan' | 'trail' | 'vortex' | 'pack' | 'bond';

export const GLYPH_IDS: readonly GlyphId[] = ['prolong', 'celerity', 'amplitude', 'focus', 'ember', 'rime', 'storm', 'venom', 'echo', 'fan', 'trail', 'vortex', 'pack', 'bond'];

/** Each button's slot opens at this hero level (short, medium, ultimate). */
export const GLYPH_SLOT_LEVELS = [12, 20, 28] as const;

/** Fragments that complete a glyph; what is left over of a complete one melts into dust. */
export const GLYPH = { fragments: 3, dustPerFragment: 20 };

/** Fragments a win gives (once a day for a crypt, as its chest): the crypt in embers and the deep one give two. */
export const GLYPH_FRAGS = { crypt: 1, cryptHard: 2, incursion: 1, boss: 1, saga: 1, expedition: 1 };

/** Where each glyph's fragments are found (docs/GLIFOS.md §3). */
export type GlyphSource = 'crypt' | 'incursion' | 'boss' | 'expedition';

/**
 * The skills whose `amount` is part of their power (a heal, a shield, a summon's life, a share of an effect); in the
 * others it is a count (turrets, hexes, jumps) or a multiplier (eagle eye's reach), and a glyph's power leaves it be.
 */
const AMOUNT_IS_POWER: ReadonlySet<SkillId> = new Set<SkillId>([
  'automaton', 'mend', 'sanctuary', 'dawn', 'aegis', 'hallow', 'bloom', 'decoy', 'repairDrone', 'faithRay', 'deepRoot', 'tuneUp', 'renew',
  'holySeal', 'sharedPrayer', 'clockHound', 'intercession', 'ancestralFury', 'avatarOfLight', 'forestAwakening', 'piercingLight', 'cleanse',
  'choir', 'atone', 'sacrifice',
]);

/** What a glyph does to a skill's power: its damage, and its amount when that is power too. */
export const potency = (skill: SkillId, bp: number): Partial<Record<SkillField, FieldPatch>> => ({
  ...(SKILLS[skill].damage > 0 ? { damage: { bp } } : {}),
  ...(AMOUNT_IS_POWER.has(skill) ? { amount: { bp } } : {}),
});

export interface GlyphDef {
  id: GlyphId;
  source: GlyphSource;
  /** Why it doesn't fit this skill (an i18n key, `glyph.no.<reason>`), or null if it fits. */
  misfit: (skill: SkillId) => 'duration' | 'area' | 'damage' | 'same' | 'lasts' | 'form' | 'aimed' | 'own' | null;
  /** A second place its fragments come from, once every glyph whose own place that is is complete (slower). */
  also?: GlyphSource;
  /** The damage type it turns the skill's into (a conversion glyph), if any. */
  turns?: DamageType;
  /** What it does to the skill's numbers, after its rank and variant. */
  patch: (skill: SkillId) => Partial<Record<SkillField, FieldPatch>>;
}

/** The forms whose radius is an area: a circle, a cone, ground, a party around, a projectile's blast, a line's width, where a leap lands, how far a chain jumps. */
const AREA = new Set(['circle', 'cone', 'zone', 'party', 'wall', 'projectile', 'line', 'leap', 'chain']);

/**
 * A conversion (docs/GLIFOS.md §1): everything the skill deals, its blows and what it leaves (ground, projectiles,
 * turrets, summons, what keeps biting), turns into `type`. It fits what deals damage of another type.
 */
const turning = (id: GlyphId, type: DamageType): GlyphDef => ({
  id,
  source: 'boss',
  turns: type,
  misfit: (sk) => (SKILLS[sk].damage <= 0 ? 'damage' : SKILL_CASTS[sk].type === type ? 'same' : null),
  patch: () => ({}),
});

/**
 * The form glyphs' numbers (docs/GLIFOS.md §1):
 * - echo: the skill again `delay` ticks later, where it was cast, at `bp` of its power;
 * - fan: three casts `spread` apart (of 1024 to a turn), each at `bp`;
 * - trail: ground of its type where it struck, `ticks` long, biting `bp` of the blow every `every`, at least `radius` wide;
 * - vortex: what it catches is dragged up to `pull` toward its middle (not bosses, the Warden or elites);
 * - pack: one summon more, every one at `bp` of its life and damage;
 * - bond: what it does on the hero or an ally reaches the nearest other ally within `reach` too, at `bp` of its length and power.
 */
export const GLYPH_FX = {
  echo: { delay: 12, bp: 4500 },
  fan: { spread: 57, bp: 4000 },
  trail: { ticks: 48, every: 12, bp: 800, radius: 1000 },
  vortex: { pull: 1000, bp: 8500 },
  pack: { bp: 5500 },
  bond: { bp: 5000, reach: 6000 },
};

/** Whether a skill's program aims at a point (not at a nightmare or an ally): a fan turns that point. */
const aimsAtPoint = (sk: SkillId): boolean =>
  SKILL_CASTS[sk].steps.some((st) => st.k === 'point' || st.k === 'aim') && !SKILL_CASTS[sk].steps.some((st) => st.k === 'enemy' || st.k === 'enemyNear' || st.k === 'ally');

const ECHO_FORMS = new Set(['strike', 'cone', 'circle', 'line', 'chain', 'projectile', 'ally', 'party']);
const TRAIL_FORMS = new Set(['strike', 'cone', 'circle', 'line', 'leap']);

export const GLYPHS: Record<GlyphId, GlyphDef> = {
  /** +40 % length, +25 % recharge: for whatever lasts. */
  prolong: {
    id: 'prolong',
    source: 'incursion',
    misfit: (sk) => (SKILLS[sk].duration > 0 ? null : 'duration'),
    patch: () => ({ duration: { bp: 14000 }, cooldown: { bp: 12500 } }),
  },
  /** −25 % recharge, −20 % power: for every skill. */
  celerity: {
    id: 'celerity',
    source: 'incursion',
    misfit: () => null,
    patch: (sk) => ({ cooldown: { bp: 7500 }, ...potency(sk, 8000) }),
  },
  /** +30 % radius (a hex more of a wall), −15 % power: for what reaches an area. */
  amplitude: {
    id: 'amplitude',
    source: 'crypt',
    misfit: (sk) => (SKILL_CASTS[sk].form === 'wall' || (SKILLS[sk].radius > 0 && AREA.has(SKILL_CASTS[sk].form)) ? null : 'area'),
    patch: (sk) => (SKILL_CASTS[sk].form === 'wall' ? { amount: { add: 1 }, ...(SKILLS[sk].damage > 0 ? { damage: { bp: 8500 } } : {}) } : { radius: { bp: 13000 }, ...potency(sk, 8500) }),
  },
  /** −35 % radius, +30 % power: for an area that strikes. */
  focus: {
    id: 'focus',
    source: 'crypt',
    misfit: (sk) => (!(SKILLS[sk].radius > 0 && ['circle', 'cone', 'zone'].includes(SKILL_CASTS[sk].form)) ? 'area' : SKILLS[sk].damage > 0 ? null : 'damage'),
    patch: (sk) => ({ radius: { bp: 6500 }, ...potency(sk, 13000) }),
  },
  ember: turning('ember', 'fire'),
  rime: turning('rime', 'frost'),
  storm: turning('storm', 'lightning'),
  venom: turning('venom', 'poison'),
  /** The skill again a second later, where it was cast, at 45 %: for what strikes at once. */
  echo: {
    id: 'echo',
    source: 'crypt',
    misfit: (sk) => (SKILLS[sk].duration > 0 ? 'lasts' : ECHO_FORMS.has(SKILL_CASTS[sk].form) ? null : 'form'),
    patch: () => ({}),
  },
  /** Three shots or lines, 20° apart, at 40 % each: for what flies or reaches in a line at a point. */
  fan: {
    id: 'fan',
    source: 'crypt',
    misfit: (sk) => (!['projectile', 'line'].includes(SKILL_CASTS[sk].form) ? 'form' : SKILLS[sk].damage <= 0 ? 'damage' : aimsAtPoint(sk) ? null : 'aimed'),
    patch: (sk) => potency(sk, GLYPH_FX.fan.bp),
  },
  /** Ground of its type where it struck, 4 s, −15 % to the blow: for what strikes. */
  trail: {
    id: 'trail',
    source: 'crypt',
    misfit: (sk) => (!TRAIL_FORMS.has(SKILL_CASTS[sk].form) ? 'form' : SKILLS[sk].damage > 0 ? null : 'damage'),
    patch: (sk) => potency(sk, 8500),
  },
  /** What it catches is dragged a hex toward its middle, −15 %: for an area that strikes. */
  vortex: {
    id: 'vortex',
    source: 'incursion',
    misfit: (sk) => (!(['circle', 'zone'].includes(SKILL_CASTS[sk].form) && SKILLS[sk].radius > 0) ? 'area' : SKILLS[sk].damage > 0 ? null : 'damage'),
    patch: (sk) => potency(sk, GLYPH_FX.vortex.bp),
  },
  /** One summon more, every one at 55 % of its life and damage. */
  /** What it does on the hero or an ally reaches the nearest other ally too, at half its length and power. */
  bond: {
    id: 'bond',
    source: 'expedition',
    also: 'boss',
    misfit: (sk) => {
      const def = SKILL_CASTS[sk];
      if (def.form !== 'self' && def.form !== 'ally') return 'form';
      // An effect of its own on the hero (the bear, the wolf, the eagle eye) can't be laid on another.
      if (def.steps.some((st) => st.k === 'special' && st.when === undefined)) return 'own';
      if (def.form === 'self' && !def.steps.some((st) => (st.k === 'set' || st.k === 'heal' || st.k === 'ward') && st.to === 'h')) return 'form';
      return null;
    },
    patch: () => ({}),
  },
  pack: {
    id: 'pack',
    source: 'incursion',
    misfit: (sk) => (SKILL_CASTS[sk].form === 'summon' ? null : 'form'),
    patch: (sk) => ({ ...potency(sk, GLYPH_FX.pack.bp), ...(SKILL_CASTS[sk].count ? { amount: { add: 1 } } : {}) }),
  },
};

/** Whether a glyph fits a skill (the screen, the server and the bots ask the same). */
export const glyphFits = (skill: SkillId, glyph: GlyphId): boolean => GLYPHS[glyph].misfit(skill) === null;

/** The glyph slots a hero of this level has open (0 to 3, short button first). */
export const glyphSlotsOpen = (level: number): number => GLYPH_SLOT_LEVELS.filter((l) => level >= l).length;

/** The damage type a hero's glyph turns this skill's into, if it carries a conversion that fits (docs/GLIFOS.md §1). */
export const glyphTurns = (skill: SkillId, glyph: GlyphId | null | undefined): DamageType | null => (glyph && GLYPHS[glyph].turns && glyphFits(skill, glyph) ? (GLYPHS[glyph].turns as DamageType) : null);
